/**
 * Firebase Authentication service for trAInedup.ai
 * Handles user registration, login, and authentication state management
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  verifyPasswordResetCode,
  confirmPasswordReset,
  verifyBeforeUpdateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  User,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInAnonymously,
  linkWithCredential,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db, functions } from './config';
import { UserRole } from '../../types';

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();

const PENDING_AUTH_METADATA_KEY = 'pending_auth_metadata';

const getPendingAuthMetadata = (): Record<string, any> => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.sessionStorage.getItem(PENDING_AUTH_METADATA_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const clearPendingAuthMetadata = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(PENDING_AUTH_METADATA_KEY);
  } catch {
    // Ignore storage errors
  }
};

/**
 * Register a new user with email and password
 */
export const registerWithEmail = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  role: UserRole = UserRole.STUDENT,
  referralCode?: string | null,
  additionalData?: Record<string, any>
) => {
  try {
    let user: User;
    const isAnonymousUser = auth.currentUser?.isAnonymous === true;

    if (isAnonymousUser && auth.currentUser) {
      // Link anonymous account to email/password — preserves UID and all assessment data
      const credential = EmailAuthProvider.credential(email, password);
      const linked = await linkWithCredential(auth.currentUser, credential);
      user = linked.user;

      await updateProfile(user, {
        displayName: `${firstName} ${lastName}`,
      });

      // Update the existing anonymous user document with real registration data
      const userData: any = {
        email: user.email,
        firstName,
        lastName,
        displayName: `${firstName} ${lastName}`,
        roles: [role],
        updatedAt: new Date(),
        isActive: true,
        emailVerified: user.emailVerified,
        profileComplete: false,
        ...additionalData,
      };

      if (referralCode) {
        userData.referredBy = referralCode;
        userData.referredAt = new Date();
      }

      await updateDoc(doc(db, 'users', user.uid), userData);

      return {
        success: true,
        user,
        userData: { uid: user.uid, subscriptionTier: 'free', createdAt: new Date(), credits: 0, ...userData },
      };
    }

    // Standard registration (no anonymous user signed in)
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    user = userCredential.user;

    // Update user profile
    await updateProfile(user, {
      displayName: `${firstName} ${lastName}`,
    });

    // Create user document in Firestore
    const userData: any = {
      uid: user.uid,
      email: user.email,
      firstName,
      lastName,
      displayName: `${firstName} ${lastName}`,
      roles: [role],
      subscriptionTier: 'free',
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true,
      emailVerified: user.emailVerified,
      profileComplete: false,
      ...additionalData,
    };

    if (referralCode) {
      userData.referredBy = referralCode;
      userData.referredAt = new Date();
    }

    await setDoc(doc(db, 'users', user.uid), userData);

    return {
      success: true,
      user: userCredential.user,
      userData: { ...userData, credits: userData.credits ?? 0 },
    };
  } catch (error: any) {
    let errorMessage = error.message;
    if (error.code === 'auth/email-already-in-use') {
      errorMessage = 'This email is already in use. Please use a different email or log in.';
    } else if (error.code === 'auth/credential-already-in-use') {
      errorMessage = 'This email is already in use. Please use a different email or log in.';
    }
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Map Firebase auth error codes to user-friendly messages for login
 */
const getLoginErrorMessage = (error: any): string => {
  switch (error.code) {
    case 'auth/invalid-credential':
      return 'Incorrect email or password. Please try again.';
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later or reset your password.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    default:
      return error.message || 'Failed to sign in. Please try again.';
  }
};

/**
 * Sign in user with email and password
 */
export const loginWithEmail = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Get user data from Firestore
    const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
    let userData = userDoc.exists() ? userDoc.data() : null;

    // Ensure all required fields are present for AuthContext
    if (userData) {
      userData = {
        uid: userData.uid || userCredential.user.uid,
        email: userData.email || userCredential.user.email || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        displayName: userData.displayName || userCredential.user.displayName || '',
        roles: userData.roles || ['student'],
        subscriptionTier: userData.subscriptionTier || 'free',
        createdAt: userData.createdAt ? (userData.createdAt.toDate ? userData.createdAt.toDate() : userData.createdAt) : new Date(),
        updatedAt: userData.updatedAt ? (userData.updatedAt.toDate ? userData.updatedAt.toDate() : userData.updatedAt) : new Date(),
        isActive: userData.isActive ?? true,
        emailVerified: userData.emailVerified ?? false,
        profileComplete: userData.profileComplete ?? false,
        intendedDestination: userData.intendedDestination || undefined,
        acquisitionSource: userData.acquisitionSource || undefined,
        skillsAssessmentCompleted: userData.skillsAssessmentCompleted ?? false,
        skillsAssessmentCompletedAt: userData.skillsAssessmentCompletedAt?.toDate ? userData.skillsAssessmentCompletedAt.toDate() : userData.skillsAssessmentCompletedAt || undefined,
        lastAssessmentDate: userData.lastAssessmentDate?.toDate ? userData.lastAssessmentDate.toDate() : userData.lastAssessmentDate || undefined,
        skillsProfile: userData.skillsProfile || undefined,
        activationCodeId: userData.activationCodeId || undefined,
        activationCodeSource: userData.activationCodeSource || undefined,
        activationCodeCampaignId: userData.activationCodeCampaignId || undefined,
        activationCodeRedeemedAt: userData.activationCodeRedeemedAt?.toDate ? userData.activationCodeRedeemedAt.toDate() : userData.activationCodeRedeemedAt || undefined,
        membershipPlan: userData.membershipPlan || undefined,
        credits: userData.credits ?? 0,
        // Add any other fields as needed
      };
    }

    return {
      success: true,
      user: userCredential.user,
      userData: userData,
    };
  } catch (error: any) {
    return {
      success: false,
      error: getLoginErrorMessage(error),
    };
  }
};

/**
 * Create or update a Firestore user document after Google sign-in and return userData.
 */
const upsertGoogleUserDoc = async (
  user: User,
  referralCode?: string | null,
  additionalData?: Record<string, any>
) => {
  const userDocRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userDocRef);
  let userData: any;

  if (!userDoc.exists()) {
    userData = {
      uid: user.uid,
      email: user.email,
      firstName: user.displayName?.split(' ')[0] || '',
      lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
      displayName: user.displayName || '',
      roles: [UserRole.STUDENT],
      subscriptionTier: 'free',
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true,
      emailVerified: user.emailVerified,
      profileComplete: false,
      photoURL: user.photoURL,
      ...additionalData,
    };

    if (referralCode) {
      userData.referredBy = referralCode;
      userData.referredAt = new Date();
    }

    await setDoc(userDocRef, userData);
  } else {
    userData = userDoc.data();
    await updateDoc(userDocRef, {
      updatedAt: new Date(),
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
      ...additionalData,
    });

    userData = {
      ...userData,
      ...additionalData,
      updatedAt: new Date(),
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
    };
  }

  return { ...userData, credits: userData.credits ?? 0 };
};

/**
 * Sign in with Google OAuth.
 * Uses popup first; falls back to redirect flow when popup is blocked (e.g. Safari/iOS).
 */
export const loginWithGoogle = async (referralCode?: string | null) => {
  try {
    const pendingAuthMetadata = getPendingAuthMetadata();
    const userCredential = await signInWithPopup(auth, googleProvider);
    const userData = await upsertGoogleUserDoc(userCredential.user, referralCode, pendingAuthMetadata);
    clearPendingAuthMetadata();
    return { success: true, user: userCredential.user, userData };
  } catch (error: any) {
    // Popup blocked or not supported — fall back to redirect
    if (
      error.code === 'auth/popup-blocked' ||
      error.code === 'auth/popup-closed-by-user' ||
      error.code === 'auth/cancelled-popup-request'
    ) {
      try {
        await signInWithRedirect(auth, googleProvider);
        // Page will reload; result is handled by handleGoogleRedirectResult on next load
        return { success: true, redirectPending: true, userData: null };
      } catch (redirectError: any) {
        return { success: false, error: redirectError.message };
      }
    }
    return { success: false, error: error.message };
  }
};

/**
 * Complete a Google redirect sign-in if the page loaded after a redirect.
 * Call this once on app startup to handle post-redirect sessions.
 */
export const handleGoogleRedirectResult = async (referralCode?: string | null) => {
  try {
    const pendingAuthMetadata = getPendingAuthMetadata();
    const result = await getRedirectResult(auth);
    if (!result) return null;
    const userData = await upsertGoogleUserDoc(result.user, referralCode, pendingAuthMetadata);
    clearPendingAuthMetadata();
    return { success: true, user: result.user, userData };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Sign out current user
 */
export const logout = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Send password reset email
 */
export const resetPassword = async (email: string, redirectUrl?: string) => {
  try {
    const callable = httpsCallable(functions, 'sendPasswordResetEmail');
    const resetUrl =
      redirectUrl ||
      process.env.REACT_APP_PASSWORD_RESET_REDIRECT_URL ||
      (typeof window !== 'undefined'
        ? `${window.location.origin}/reset-password`
        : undefined);
        
    const response = await callable({ email, redirectUrl: resetUrl });
    const data = response.data as { success?: boolean; error?: string };

    if (data?.success) {
      return { success: true };
    }
  } catch (error: any) {
    console.error('Callable password reset failed, falling back to Firebase auth:', error);
  }

  try {
    const resetUrl =
      redirectUrl ||
      process.env.REACT_APP_PASSWORD_RESET_REDIRECT_URL ||
      (typeof window !== 'undefined'
        ? `${window.location.origin}/reset-password`
        : '/reset-password');

    await sendPasswordResetEmail(auth, email, {
      url: resetUrl,
      handleCodeInApp: true,
    });
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Verify password reset code and return associated email
 */
export const verifyResetCode = async (oobCode: string) => {
  try {
    const email = await verifyPasswordResetCode(auth, oobCode);
    return { success: true, email };
  } catch (error: any) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Confirm password reset with new password
 */
export const confirmResetPassword = async (oobCode: string, newPassword: string) => {
  try {
    await confirmPasswordReset(auth, oobCode, newPassword);
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (
  userId: string,
  updates: Partial<{
    firstName: string;
    lastName: string;
    displayName: string;
    bio: string;
    company: string;
    jobTitle: string;
    location: string;
    website: string;
    linkedin: string;
    twitter: string;
    profileComplete: boolean;
  }>
) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      ...updates,
      updatedAt: new Date(),
    });

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get current user data from Firestore
 */
export const getCurrentUserData = async (userId: string) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (userDoc.exists()) {
      return {
        success: true,
        userData: userDoc.data(),
      };
    } else {
      return {
        success: false,
        error: 'User document not found',
      };
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Listen to authentication state changes
 */
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Initiate email update with verification
 */
export const updateUserEmail = async (newEmail: string) => {
  try {
    if (!auth.currentUser) {
      return { success: false, error: 'No user logged in' };
    }

    // Check if email is already in use by another account in Firestore
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', newEmail));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      // Check if the found user is the current user (in case of case sensitivity or other matches)
      const isCurrentUser = querySnapshot.docs.some(doc => doc.id === auth.currentUser?.uid);
      if (!isCurrentUser) {
        return { success: false, error: 'This email is already in use by another account.' };
      }
    }

    await verifyBeforeUpdateEmail(auth.currentUser, newEmail);
    return { success: true };
  } catch (error: any) {
    let errorMessage = error.message;
    if (error.code === 'auth/email-already-in-use') {
      errorMessage = 'This email is already in use by another account.';
    } else if (error.code === 'auth/requires-recent-login') {
      errorMessage = 'For security reasons, please confirm your password to change your email.';
    }
    return { success: false, error: errorMessage, errorCode: error.code };
  }
};

/**
 * Re-authenticate user with password
 */
export const reauthenticateUser = async (password: string) => {
  try {
    if (!auth.currentUser || !auth.currentUser.email) {
      return { success: false, error: 'No user logged in' };
    }

    const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
    await reauthenticateWithCredential(auth.currentUser, credential);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Sync user email from Auth to Firestore
 */
export const syncUserEmail = async (userId: string, email: string, emailVerified: boolean) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      email,
      emailVerified,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error syncing user email:', error);
  }
};

/**
 * Sign in the current visitor anonymously so they can use the Skills Assessment
 * without a full account. Creates a minimal Firestore user document so the
 * assessment service can read/write user data. The anonymous account will be
 * linked to a real email/password account when the user registers.
 */
export const signInAnonymousUser = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    // If already signed in (anonymous or real), do nothing
    if (auth.currentUser) {
      return { success: true };
    }
    const result = await signInAnonymously(auth);
    const uid = result.user.uid;

    // Create minimal user document so assessment service writes succeed.
    // Fire-and-forget: a Firestore write failure should not block the sign-in.
    setDoc(doc(db, 'users', uid), {
      uid,
      email: '',
      firstName: '',
      lastName: '',
      displayName: '',
      roles: ['student'],
      subscriptionTier: 'free',
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true,
      emailVerified: false,
      profileComplete: false,
    }, { merge: true }).catch((err) => {
      console.warn('Could not create anonymous user document:', err);
    });

    return { success: true };
  } catch (error: any) {
    console.error('Anonymous sign-in error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Auth service object for easier importing
 */
export const authService = {
  registerWithEmail,
  loginWithEmail,
  loginWithGoogle,
  logout,
  resetPassword,
  updateUserProfile,
  getCurrentUserData,
  onAuthStateChange,
  updateUserEmail,
  syncUserEmail,
  reauthenticateUser,
};

// Default export
export default authService;

